import Icon from "@core-components/icon/Icon";
import useAppTranslation from "@hooks/language/useAppTranslation";
import { EBreakpoints, useBreakpoint } from "@hooks/useBreakpoint";
import {
  Card,
  Center,
  SegmentedControl,
  SimpleGrid,
  TextInput,
} from "@mantine/core";
import type {
  IRankedChampion,
  IRankedSkin,
} from "@modules/ranked/models/IRankedSmurf.models";
import { IconSearch } from "@tabler/icons-react";
import { Fragment, useMemo, useState } from "react";
import SkinAndChampionCard from "./SkinAndChampionCards";

interface ISkinsAndChampionsPreviewProps {
  champions: IRankedChampion[];
  skinShards: IRankedSkin[];
  permanentSkins: IRankedSkin[];
}

export const SkinsAndChampionsPreview: React.FC<
  ISkinsAndChampionsPreviewProps
> = ({ champions, skinShards, permanentSkins }) => {
  const [selectedTab, setSelectedTab] = useState("champions");
  const [search, setSearch] = useState("");
  const { breakpoint } = useBreakpoint();
  const { t } = useAppTranslation("components.skinsAndChampionsPreview");

  const cardsData = useMemo(() => {
    switch (selectedTab) {
      case "champions":
        return champions;
      case "skinShards":
        return skinShards;
      case "permanentSkins":
        return permanentSkins;
      default:
        return [];
    }
  }, [selectedTab]);

  return (
    <Card
      withBorder
      classNames={{
        root: "p-0 bg-primary-darker border-primary-lighter rounded-lg xl:basis-1/2 grow",
      }}
    >
      <SegmentedControl
        classNames={{
          root: "p-4 gap-2 !m-0 bg-primary-darker mx-auto",
          indicator:
            "border border-primary-lighter bg-transparent z-10 before:content-[' '] before:absolute before:bg-primary-dark-text " +
            "max-sm:before:-left-4 max-sm:before:h-6 max-sm:before:w-1 max-sm:before:rounded-full max-sm:before:-translate-y-[50%] max-sm:before:top-1/2 " +
            "md:before:-bottom-4 md:before:w-12 md:before:h-1 md:before:rounded-t-full md:before:-translate-x-[50%] md:before:left-1/2 ",
          label:
            "text-primary-dark-text bg-primary-default my-1 md:mx-3 data-[active=true]:bg-primary-light data-[active=true]:text-primary-light-text hover:text-primary-light-text !m-0",
        }}
        data={[
          {
            value: "champions",
            label: (
              <Center style={{ gap: 10 }}>
                {selectedTab === "champions" ? (
                  <Icon.SkinsCard className="max-w-[25px] shrink-0 grow" />
                ) : (
                  <Icon.UserSelected className="max-w-[25px] shrink-0 grow" />
                )}
                <span>{t("champions")}</span>
                <div className="rounded bg-primary-lighter px-2 py-1">
                  {champions.length}
                </div>
              </Center>
            ),
          },
          {
            value: "skinShards",
            label: (
              <Center style={{ gap: 10 }}>
                {selectedTab === "skinShards" ? (
                  <Icon.MaskSelected className="max-w-[27px] shrink-0 grow" />
                ) : (
                  <Icon.MaskNotSelected className="max-w-[27px] shrink-0 grow" />
                )}
                <span>{t("skinShards")}</span>
                <div className="rounded bg-primary-lighter px-2 py-1">
                  {skinShards.length}
                </div>
              </Center>
            ),
          },
          {
            value: "permanentSkins",
            label: (
              <Center style={{ gap: 10 }}>
                {selectedTab === "permanentSkins" ? (
                  <Icon.MaskSelected className="max-w-[27px] shrink-0 grow" />
                ) : (
                  <Icon.MaskNotSelected className="max-w-[27px] shrink-0 grow" />
                )}{" "}
                <span>{t("permanentSkins")}</span>
                <div className="rounded bg-primary-lighter px-2 py-1">
                  {permanentSkins.length}
                </div>
              </Center>
            ),
          },
        ]}
        orientation={breakpoint <= EBreakpoints.XS ? "vertical" : "horizontal"}
        value={selectedTab}
        withItemsBorders={false}
        onChange={setSelectedTab}
      />
      <TextInput
        classNames={{
          root: "px-8 py-3 bg-primary-light",
          input: "bg-primary-darker text-primary-light-text border-none",
        }}
        leftSection={<IconSearch />}
        maw={"none"}
        placeholder={t("searchPlaceholder")}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div className="h-[500px] overflow-y-scroll">
        <SimpleGrid
          classNames={{
            root: "p-8",
          }}
          cols={{ lg: 7, md: 6, sm: 5, xs: 4, base: 2 }}
        >
          {cardsData
            .filter((item) =>
              item.name.toLowerCase().includes(search.toLowerCase()),
            )
            .map((item, index) => (
              <Fragment key={index}>
                <SkinAndChampionCard
                  imageSrc={item.thumbnail}
                  isSkin={selectedTab !== "champions"}
                  name={item.name}
                />
              </Fragment>
            ))}
        </SimpleGrid>
      </div>
    </Card>
  );
};
